import React from "react";
import { Camera } from "lucide-react";

/**
 * PhotoDrop — editorial photo picker with drag & drop and inline preview.
 * Holds the File object in the parent via onChange(file | null).
 */
const PhotoDrop = ({ file, existingUrl, onChange, testId }) => {
  const inputRef = React.useRef(null);
  const [dragOver, setDragOver] = React.useState(false);

  const previewUrl = React.useMemo(() => {
    if (file) return URL.createObjectURL(file);
    return existingUrl || null;
  }, [file, existingUrl]);

  React.useEffect(() => {
    return () => {
      if (previewUrl && file) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl, file]);

  const handleFiles = (files) => {
    const f = files && files[0];
    if (f) onChange(f);
  };

  return (
    <div>
      <button
        type="button"
        data-testid={testId}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
        className={`w-full border border-dashed transition-colors text-left p-6 md:p-8 flex items-center gap-6 ${
          dragOver
            ? "border-[#63846F] bg-[#63846F]/5"
            : "border-[#3F403A]/25 hover:border-[#63846F] bg-transparent"
        }`}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Vista previa de tu foto"
            className="h-24 w-24 md:h-28 md:w-28 object-cover shrink-0"
            data-testid={`${testId}-preview`}
          />
        ) : (
          <span className="h-24 w-24 md:h-28 md:w-28 shrink-0 flex items-center justify-center bg-[#F4EFE7] text-[#63846F]">
            <Camera className="h-7 w-7" strokeWidth={1.5} />
          </span>
        )}
        <span>
          <span className="block font-serif text-xl md:text-2xl text-[#3F403A] leading-tight">
            {previewUrl ? "Tu foto está lista" : "Sube tu foto"}
          </span>
          <span className="mt-2 block font-sans text-sm text-[#3F403A]/60">
            {previewUrl
              ? "Pulsa para cambiarla · "
              : "o arrástrala aquí · "}
            mejor primer plano, con luz · JPG, PNG o WEBP (máx 6 MB)
          </span>
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        data-testid={`${testId}-input`}
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
};

export default PhotoDrop;
